import { Col, Nav, Row, Stack, Tab } from 'react-bootstrap'
import { ProductAvatar } from '../ProductImage/ProductAvatar'
import {
  HorizontalStack,
  IconValueBlock,
  ImageLoadingPlaceholder,
  LoadingPlaceholder,
  Scrollable,
  ShowIf,
  TooltipPopOver,
} from '@ztechapps/webui.util'
import { MetadataNewInput } from '../Metadata'
import { AppSettingKey, useProductById } from '@ztechapps/product.common'
import React from 'react'
import { useProductTooltipPopOverContext } from './Context'
import { ProductTooltipPopOverInlineInput } from './ProductTooltipPopOverInlineInput'
import { productQueries } from './graphql'
import { CodeInlineInput } from '../InlineInput/CodeInlineInput'
import { PriceInlineInput } from '../InlineInput/PriceInlineInput'
import { OriginalPriceInlineInput } from '../InlineInput/OriginalPriceInlineInput'
import { ProductTypeInlineInput } from '../InlineInput/ProductTypeInlineInput'
import { NameInlineInput } from '../InlineInput/NameInlineInput'
import { ProductQuantity } from '../ProductQuantity'
import { ProductStatusInlineInput } from '../InlineInput/ProductStatusInlineInput'
import { BillOfMaterialsProvider } from '../BillOfMaterials/Context'
import { BomDetails } from '../BillOfMaterials/BomDetails'
import { ProductUnitOfMeasure } from '../UnitOfMeasure/product-uom/ProductUnitOfMeasure'

export const Body = () => {
  const { productId } = useProductTooltipPopOverContext()

  const productDocument = useProductById({
    _id: productId,
    query: productQueries.singleConfig.query,
  })

  const loading = productDocument.loading
  const product = productDocument?.document
  const hasSubComponents = product?.hasSubComponents
  const defaultActiveKey = hasSubComponents ? 'bom' : 'attributes'

  return (
    <div style={{ width: '30vw', maxHeight: '80vh' }}>
      <Stack gap={1}>
        <Row>
          <Col md={2}>
            <div className="align-self-center">
              <LoadingPlaceholder loading={loading} as={ImageLoadingPlaceholder}>
                <ProductAvatar
                  name={product?.name}
                  coverImage={product?.coverImage}
                  size={80}
                  coverImageThumb={product?.coverImageThumb}
                />
              </LoadingPlaceholder>
            </div>
          </Col>
          <Col md={0}>
            <Stack gap={1}>
              <LoadingPlaceholder loading={loading}>
                <HorizontalStack gap={1} className="justify-content-between">
                  <div className="flex-fill">
                    <ProductTooltipPopOverInlineInput
                      name={'code'}
                      parentInputComponent={CodeInlineInput}
                      displayContent={(e, onClick) => (
                        <TooltipPopOver tooltipBody="Product code">
                          <h4 className={'m-0'}>{CodeInlineInput.displayContent(e, onClick)}</h4>
                        </TooltipPopOver>
                      )}
                      productDocument={productDocument}
                    />
                  </div>
                  <h4 className="m-0">
                    <ProductQuantity
                      hasSubComponents={product?.hasSubComponents}
                      quantityOnHand={product?.inventory?.quantityOnHand}
                      requiredComputedQuantityOnHand={product?.requiredComputedQuantityOnHand}
                      baseUomSymbol={product?.baseUom?.symbol}
                      decimalPrecision={product?.unitOfMeasurePrecision}
                    />
                  </h4>
                </HorizontalStack>
              </LoadingPlaceholder>
              <LoadingPlaceholder loading={loading}>
                <ProductTooltipPopOverInlineInput
                  name={'name'}
                  parentInputComponent={NameInlineInput}
                  displayContent={(e) => (
                    <TooltipPopOver tooltipBody="Product Name / Description">
                      <div className={''}>
                        <IconValueBlock icon="box" value={e} />
                      </div>
                    </TooltipPopOver>
                  )}
                  productDocument={productDocument}
                />
              </LoadingPlaceholder>
              <Stack gap={2}>
                <Row>
                  <Col>
                    <LoadingPlaceholder loading={loading}>
                      <ProductTooltipPopOverInlineInput
                        parentInputComponent={ProductTypeInlineInput}
                        name="productType"
                        productDocument={productDocument}
                      />
                    </LoadingPlaceholder>
                  </Col>
                  <Col>
                    <LoadingPlaceholder loading={loading}>
                      <ProductTooltipPopOverInlineInput
                        parentInputComponent={ProductStatusInlineInput}
                        name="status"
                        productDocument={productDocument}
                      />
                    </LoadingPlaceholder>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <LoadingPlaceholder loading={loading}>
                      <ProductTooltipPopOverInlineInput
                        name={'price'}
                        parentInputComponent={PriceInlineInput}
                        productDocument={productDocument}
                      />
                    </LoadingPlaceholder>
                  </Col>

                  <Col>
                    <LoadingPlaceholder loading={loading}>
                      <ProductTooltipPopOverInlineInput
                        name={'originalPrice'}
                        parentInputComponent={OriginalPriceInlineInput}
                        productDocument={productDocument}
                      />
                    </LoadingPlaceholder>
                  </Col>
                </Row>
              </Stack>
            </Stack>
          </Col>

          <Col md={12}>
            <hr className="my-1" />
            <ProductUnitOfMeasure
              product={product}
              InlineInputComponent={ProductTooltipPopOverInlineInput}
              inlineInputProps={{ productDocument }}
            />
          </Col>
        </Row>

        <LoadingPlaceholder loading={loading}>
          <Tab.Container defaultActiveKey={defaultActiveKey}>
            <Nav variant="underline" className="mb-3 bg-body ">
              <ShowIf condition={hasSubComponents}>
                <Nav.Item>
                  <Nav.Link eventKey="bom">Sub Assemblies</Nav.Link>
                </Nav.Item>
              </ShowIf>

              <Nav.Item>
                <Nav.Link eventKey="attributes">Specifications</Nav.Link>
              </Nav.Item>
            </Nav>

            <Scrollable height={'50vh'}>
              <Tab.Content>
                <ShowIf condition={hasSubComponents}>
                  <Tab.Pane eventKey="bom">
                    <BillOfMaterialsProvider product={product} canEdit={false}>
                      <BomDetails />
                    </BillOfMaterialsProvider>
                  </Tab.Pane>
                </ShowIf>

                <Tab.Pane eventKey="attributes">
                  <MetadataNewInput
                    _id={product?._id}
                    value={product?.metadata}
                    appSettingKey={AppSettingKey.PRODUCT_SPECIFICATIONS}
                    headerName={null}
                    compact
                  />
                </Tab.Pane>
              </Tab.Content>
            </Scrollable>
          </Tab.Container>
        </LoadingPlaceholder>
      </Stack>
    </div>
  )
}
